/**
 * File extension mappings for S3 filenames that use underscore instead of dot
 * e.g., "document_pdf" -> "document.pdf"
 */
const EXTENSION_SUFFIXES = ['pdf', 'jpeg', 'jpg', 'png', 'gif', 'webp', 'svg', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'mp4', 'mov', 'avi', 'mp3', 'wav', 'msg']

/**
 * Normalizes a filename by converting underscore-based extensions to proper dot-based extensions.
 * Handles S3 filenames that end with _ext (e.g., "file_pdf" -> "file.pdf")
 */
export function normalizeFilename(filename: string): string {
  if (!filename) return filename

  const lowerFilename = filename.toLowerCase()

  for (const ext of EXTENSION_SUFFIXES) {
    if (lowerFilename.endsWith(`_${ext}`)) {
      // Replace the last occurrence of _ext with .ext
      const lastIndex = filename.toLowerCase().lastIndexOf(`_${ext}`)
      return filename.substring(0, lastIndex) + '.' + ext
    }
  }

  return filename
}

/**
 * Gets the file extension from a filename (handles both _ext and .ext formats)
 */
export function getFileExtension(filename: string): string | null {
  if (!filename) return null

  const normalized = normalizeFilename(filename)
  const lastDotIndex = normalized.lastIndexOf('.')
  if (lastDotIndex === -1) return null

  return normalized.substring(lastDotIndex + 1).toLowerCase()
}

/**
 * Checks if a file is a PDF based on its filename or mime type
 */
export function isPdfFile(filename: string, mimeType?: string | null): boolean {
  if (mimeType) return mimeType === 'application/pdf'
  if (!filename) return false
  const ext = getFileExtension(filename)
  return ext === 'pdf'
}

/**
 * Checks if a file is an image based on its filename or mime type
 */
export function isImageFile(filename: string, mimeType?: string | null): boolean {
  if (mimeType) return mimeType.startsWith('image/')
  if (!filename) return false
  const ext = getFileExtension(filename)
  // heic/heif included so HEIC photos route through the resize-worker (which
  // transcodes to a browser-renderable format) instead of being served raw.
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic', 'heif'].includes(
    ext || '',
  )
}

/**
 * Get file extension from a MIME content type (images only, defaults to 'jpg')
 */
export function getExtensionFromMimeType(contentType: string): string {
  const extensionMap: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
  }
  return extensionMap[contentType] || 'jpg'
}

/**
 * Get file extension from content type or filename (supports all file types).
 * Tries the filename first, then falls back to MIME type mapping.
 */
export function getExtensionFromMimeTypeOrFilename(contentType: string, filename?: string): string {
  // Try to get extension from filename first
  if (filename) {
    const match = filename.match(/\.([a-zA-Z0-9]+)$/)
    if (match) {
      return match[1].toLowerCase()
    }
  }

  // Fall back to content type mapping
  const extensionMap: Record<string, string> = {
    // Images
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    // Documents
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    // Text
    'text/plain': 'txt',
    'text/csv': 'csv',
    'text/html': 'html',
    // Archives
    'application/zip': 'zip',
    'application/x-rar-compressed': 'rar',
    // Other
    'application/json': 'json',
    'application/xml': 'xml',
  }

  return extensionMap[contentType] || 'bin'
}

/**
 * Checks if a file is an Outlook .msg file based on its filename or mime type
 */
export function isMsgFile(filename: string, mimeType?: string | null): boolean {
  if (mimeType) return mimeType === 'application/vnd.ms-outlook'
  if (!filename) return false
  const ext = getFileExtension(filename)
  return ext === 'msg'
}
